import type {ReactElement} from 'react';
import {useEffect, useRef} from 'react';
import {noop} from '@Utils/general.util';
import {Button, IconPlacement, Size, Type} from '@Components/button';
import {LogoWithBackBtn} from '@Components/logo-with-back-btn';
import {
  AddAccountEvent,
  BLURABLE_CLASS_IN_AI_CAPTION_MODE,
  ContinueEditingEvent,
  SocialMediaWizardStep,
  WizardMobileNavView,
} from '@Components/social-media-wizard/social-media-wizard.types';
import useDidUpdateEffect from '@Hooks/useDidUpdateEffect';
import {
  useAccountIdsToPublishTo,
  useAccountsToPublishTo,
  useNextButtonDisabled,
  useNextButtonIcon,
  useNextButtonText,
  usePublishingStatusForAllAccounts,
  useSocialMediaWizardPost,
  useWizardStep,
} from '@Components/social-media/social-media.hooks';
import type {ContainerQueryObject} from '@Hooks/useContainerQuery';
import {useContainerQuery} from '@Hooks/useContainerQuery';
import {PopupMenuItem} from '@Components/popup-menu/components';
import {PopupMenu} from '@Components/popup-menu';
import {Icon} from '@Components/icon-v2';
import {IconShape, IconSize, IconType} from '@Components/icon-v2/icon.types';
import type {ViewToggleBtnConfig} from '@Components/social-media-wizard/components/social-media-wizard-view-toggle';
import {SocialMediaWizardViewToggle} from '@Components/social-media-wizard/components/social-media-wizard-view-toggle';
import {
  resetStateToDraft,
  setWizardMobileNavView,
  updatePublishingAction,
  updateTriggerNext,
} from '@Components/social-media-wizard/social-media-wizard-reducer';
import {PlatformMediaType, PostState} from '@Components/social-media/post.vo';
import {Text, TextSize} from '@Components/text';
import {SocialPostPublishingAction} from '@Components/social-media/publish-params.types';
import {
  useAreCaptionsBeingAutoGenerated,
  useHasPublishingFinished,
  useHideNextButton,
  useIsTemplateSelected,
  useWizardCaptionErrors,
  useWizardDesignsGraphicsMetaMap,
  useWizardPublishingAction,
  useWizardTitleErrors,
} from '@Components/social-media-wizard/social-media-wizard.hooks';
import {isUserLoggedIn, isUserPremium} from '@Libraries/user.library';
import {getPremiumUpsellIconClasses} from '@Libraries/premium-library';
import {LoadingStates} from '@Utils/loading.util';
import {useWizardCoverCtaPulse, useWizardDraftScheduleActions, useWizardNavStep} from './social-media-wizard-nav.hooks';
import OverlayTrigger from 'react-bootstrap/OverlayTrigger';
import Tooltip from 'react-bootstrap/Tooltip';
import {useAppDispatch} from '@/hooks';
import styles from './social-media-wizard-nav.module.scss';
import {SocialMediaProgressBar, SocialMediaProgressBarSize} from '../../../social-media/components/social-media-progress-bar';
import {showLoading} from '@Libraries/loading-toast-library';
import type {DesignSelection} from '@Components/social-media/social-media.types';
import {WizardScheduleControls} from '../wizard-schedule-controls/wizard-schedule-controls';
import {WizardDateTimePicker} from '../wizard-date-time-picker/wizard-date-time-picker';
import {SocialMediaTestId} from '@/test-ids/social-media-test-id';

const containerQuery: ContainerQueryObject = {
  xsmall: {
    minWidth: 0,
    maxWidth: 480,
  },
  small: {
    minWidth: 481,
    maxWidth: 783,
  },
  medium: {
    minWidth: 784,
    maxWidth: 1040,
  },
  large: {
    minWidth: 1040,
  },
  xlarge: {
    minWidth: 1200,
  },
};

const ANIMATABLE_ICON_CLASS = 'icon-publish-arrow';

interface SocialMediaWizardNavProps {
  navSteps?: string[];
  onNext?: VoidFunction;
  onBack?: VoidFunction;
  currentSelectedDesigns?: DesignSelection[];
}

export function SocialMediaWizardNav({navSteps = [], onNext = noop, onBack = noop, currentSelectedDesigns = []}: SocialMediaWizardNavProps): ReactElement<any> {
  const isNextButtonDisabled = useNextButtonDisabled();
  const nextButtonStateText = useNextButtonText();
  const nextButtonStateIcon = useNextButtonIcon();
  const dispatch = useAppDispatch();
  const nextButtonDefaultText = window.i18next.t('pmwjs_next');
  const containerRef = useRef<HTMLDivElement>(null);
  const params = useContainerQuery(containerRef, containerQuery);
  const showBrowserConfirmOnExit = useRef<boolean>(false);
  const socialPost = useSocialMediaWizardPost();
  const allPublishingStatus = usePublishingStatusForAllAccounts();
  const publishingAction = useWizardPublishingAction();
  const currentStepFromRedux = useWizardStep();
  const accountIdsToPublishTo = useAccountIdsToPublishTo();
  const accountsToPublishTo = useAccountsToPublishTo();
  const designGraphicsMetaMap = useWizardDesignsGraphicsMetaMap();
  const titleErrors = useWizardTitleErrors();
  const hideNextButton = useHideNextButton();
  const captionErrors = useWizardCaptionErrors();
  const hasPublishingFinished = useHasPublishingFinished();
  const isTemplateSelected = useIsTemplateSelected();
  const isSelectTemplateFlow = !isUserLoggedIn() || isTemplateSelected;
  const areCaptionsBeingAutoGenerated = useAreCaptionsBeingAutoGenerated();
  const {currNavStep, newNavStep} = useWizardNavStep({
    wizardStep: currentStepFromRedux,
    isSelectTemplateFlow,
    navSteps,
  });
  const {
    showPulsatingDotForPreviewCTA,
    showHightlightPulseForViewToggle,
    shouldTriggerPreviewCTAPulses,
    resetPreviewCTAPulsesStates,
    triggerPreviewCTAPulsesIfNeeded,
    hidePulsatingDotForPreviewCTAIfNeeded,
  } = useWizardCoverCtaPulse({
    currentStep: currentStepFromRedux,
    isMobileScreen: params.xsmall || params.small,
    designGraphicsMetaMap,
    accountsToPublishTo,
  });

  const {
    isDateTimePickerVisible,
    setIsDateTimePickerVisible,
    onSaveAsDraft,
    onSchedule,
    onSchedulingDone,
    onDeleteSchedule,
  } = useWizardDraftScheduleActions({
    socialPost,
    accountIdsToPublishTo,
    captionErrors,
    titleErrors,
  });

  let nextButtonText = nextButtonDefaultText;
  let nextButtonIcon = nextButtonStateIcon;

  if (nextButtonStateIcon) {
    nextButtonIcon = nextButtonStateIcon;
  }

  if (nextButtonStateText) {
    nextButtonText = nextButtonStateText;
  }

  const resetNavState = (): void => {
    dispatch(setWizardMobileNavView(WizardMobileNavView.CONTENT));
  };

  const didAllAccountsErrorOut = (): boolean => {
    let allAccountsErroredOut = true;

    if (!allPublishingStatus) {
      return false;
    }

    accountIdsToPublishTo.forEach((accountIdToPublishTo) => {
      const publishingStatusForAccount = allPublishingStatus[accountIdToPublishTo];

      if (!publishingStatusForAccount) {
        return;
      }

      const hasSomePostBeenPublished =
        publishingStatusForAccount[PlatformMediaType.POST] !== LoadingStates.NOT_LOADED ||
        publishingStatusForAccount[PlatformMediaType.REELS] !== LoadingStates.NOT_LOADED ||
        publishingStatusForAccount[PlatformMediaType.STORIES] !== LoadingStates.NOT_LOADED;

      if (allAccountsErroredOut && hasSomePostBeenPublished) {
        allAccountsErroredOut = false;
      }
    });

    return allAccountsErroredOut;
  };

  const onLogoClick = (): void => {
    if (showBrowserConfirmOnExit.current) {
      const shouldLeave = window.confirm(window.i18next.t('pmwjs_you_have_unsaved_changes'));
      if (!shouldLeave) {
        return;
      }

      showBrowserConfirmOnExit.current = false;
    }

    const loadingKey = 'mystuff-redirect';
    const saveToDraftWait = 1000;
    showLoading(loadingKey, {text: window.i18next.t('pmwjs_redirecting_to_my_stuff')});
    savePostAsDraftIfNeeded();

    setTimeout(() => {
      window.location.href = window.PMW.util.site_url('');
    }, saveToDraftWait);
  };

  const savePostAsDraftIfNeeded = (): void => {
    if (!publishingAction || publishingAction === SocialPostPublishingAction.SAVE) {
      dispatch(updatePublishingAction(SocialPostPublishingAction.SAVE));
      dispatch(updateTriggerNext());
    }
  };

  const onBackClick = (): void => {
    resetNavState();

    if (didAllAccountsErrorOut()) {
      dispatch(resetStateToDraft());
    }

    onBack();
  };

  const doHideBackButton = (): boolean => {
    return isFinalStep() && !didAllAccountsErrorOut();
  };
  const isFinalStep = (): boolean => {
    return currentStepFromRedux === SocialMediaWizardStep.PUBLISH;
  };

  const isMobileScreen = (): boolean => {
    return params.xsmall || params.small;
  };

  const getLogoWithButton = (): ReactElement<any> => {
    return <LogoWithBackBtn onBack={onBackClick} isMobileLogo={isMobileScreen()} onLogoClick={onLogoClick} hideBackButton={doHideBackButton()} />;
  };

  const getProgressBar = (): ReactElement<any> | null => {
    if (isMobileScreen()) {
      return null;
    }

    return (
      <SocialMediaProgressBar
        size={params.medium ? SocialMediaProgressBarSize.SMALL : SocialMediaProgressBarSize.LARGE}
        className={styles.navSteps}
        currentStep={currNavStep.current}
        labels={navSteps}
        newStep={newNavStep.current}
      />
    );
  };

  const getIconMenuMarkup = (): ReactElement => {
    if (params.medium && shouldShowScheduleDetails()) {
      return <Icon icon="icon icon-more-h" size={IconSize.SIZE_ICON_16} type={IconType.DARK_SECONDARY} shape={IconShape.SQUARE} isDisabled={areCaptionsBeingAutoGenerated} />;
    }
    return (
      <Icon
        id="publish-options-menu-icon"
        className="spacing-m-r-2"
        icon="icon-more-h"
        size={IconSize.SIZE_ICON_20}
        shape={IconShape.SQUARE}
        type={IconType.GHOST}
        isDisabled={areCaptionsBeingAutoGenerated}
      />
    );
  };

  const getSaveAsDraftButton = (): ReactElement<any> => {
    return (
      <Button
        customClasses="spacing-m-r-2"
        type={Type.DARK_SECONDARY}
        size={Size.SMALL}
        onClick={onSaveAsDraft}
        iconPlacement={IconPlacement.LEFT}
        icon="icon-save"
        text={window.i18next.t('pmwjs_draft')}
        disabled={areCaptionsBeingAutoGenerated}
      />
    );
  };

  const getScheduleDetailsPanelMenu = (): ReactElement<any> => {
    return (
      <>
        {params.xlarge ? getSaveAsDraftButton() : null}
        <WizardScheduleControls
          socialPost={socialPost}
          isLargeBreakpoint={params.large}
          isMediumBreakpoint={params.medium}
          areCaptionsBeingAutoGenerated={areCaptionsBeingAutoGenerated}
          onDeleteSchedule={onDeleteSchedule}
          onOpenDateTimePicker={(): void => {
            setIsDateTimePickerVisible(true);
          }}
        />
      </>
    );
  };

  const getDetailsPanelMenu = (): ReactElement<any> => {
    let scheduleIconClasses = 'icon icon-calendar';
    const isPAYGUser = !isUserPremium();

    if (isPAYGUser) {
      scheduleIconClasses = getPremiumUpsellIconClasses();
    }

    if (params.xlarge) {
      return (
        <>
          {getSaveAsDraftButton()}
          <Button
            data-testid={SocialMediaTestId.WIZARD_SCHEDULE_BUTTON}
            customClasses="spacing-m-r-2"
            type={isPAYGUser ? Type.PREMIUM : Type.DARK_SECONDARY}
            size={Size.SMALL}
            onClick={onSchedule}
            iconPlacement={IconPlacement.LEFT}
            icon="icon-calendar"
            text={window.i18next.t('pmwjs_schedule')}
            disabled={areCaptionsBeingAutoGenerated}
          />
        </>
      );
    }

    return (
      <div id="popup-menu">
        <PopupMenu menuToggleButton={getIconMenuMarkup()} maxWidth={undefined} scrollingContainerSelector={undefined}>
          <PopupMenuItem text={window.i18next.t('pmwjs_save_as_draft')} iconClasses="icon icon-save" clickHandler={onSaveAsDraft} />
          <PopupMenuItem text={window.i18next.t('pmwjs_schedule_for_later')} iconClasses={scheduleIconClasses} clickHandler={onSchedule} />
        </PopupMenu>
      </div>
    );
  };

  const shouldShowScheduleDetails = (): boolean => {
    if (!socialPost?.state) {
      return false;
    }

    if (
      socialPost.state === PostState.SCHEDULED ||
      socialPost.state === PostState.PUBLISHING ||
      socialPost.state === PostState.DRAFT ||
      socialPost.state === PostState.ERROR ||
      socialPost.state === PostState.SCHEDULE_ERROR
    ) {
      return false;
    }

    return !!(socialPost?.publishOn && socialPost.timeZoneOffset !== null && currentStepFromRedux === SocialMediaWizardStep.ADD_DETAILS);
  };

  const triggerPreviousOnAddAccount = (): void => {
    onBackClick();
  };

  const triggerPreviousOnContinueEditing = (): void => {
    resetNavState();
  };

  useEffect(() => {
    window.addEventListener(AddAccountEvent.ADD_ACCOUNT, triggerPreviousOnAddAccount);
    window.addEventListener(ContinueEditingEvent.CONTINUE_EDITING, triggerPreviousOnContinueEditing);
    window.addEventListener('beforeunload', confirmNavigation);

    return (): void => {
      window.removeEventListener(AddAccountEvent.ADD_ACCOUNT, triggerPreviousOnAddAccount);
      window.removeEventListener(ContinueEditingEvent.CONTINUE_EDITING, triggerPreviousOnContinueEditing);
      window.removeEventListener('beforeunload', confirmNavigation);
    };
  }, []);

  const confirmNavigation = (event: BeforeUnloadEvent): void => {
    if (showBrowserConfirmOnExit.current) {
      event.preventDefault();
      event.stopPropagation();
      return;
    }
  };

  useDidUpdateEffect(() => {
    showBrowserConfirmOnExit.current = currentStepFromRedux === SocialMediaWizardStep.ADD_DETAILS;

    resetNavState();
    resetPreviewCTAPulsesStates();
    triggerPreviewCTAPulsesIfNeeded();
  }, [currentStepFromRedux]);

  const onPremiumUpsell = (): void => {
    void window.PMW.showPremiumOnlyFeatureDialog(window.PMW.PREMIUM_ONLY_SOCIAL_MEDIA_MULTI_POSTING as string);
  };

  const onNextButtonClick = (): void => {
    if (shouldShowPremiumUpselling()) {
      onPremiumUpsell();
      return;
    }

    onNext();
  };

  const getBackToMyStuffButton = (): ReactElement<any> => {
    return (
      <Button
        disabled={!hasPublishingFinished}
        customClasses="-noun"
        type={Type.SECONDARY}
        size={Size.SMALL}
        text={window.i18next.t('pmwjs_back_to_my_posts')}
        onClick={(): void => {
          window.location.href = window.PMW.util.site_url('posters/mine#/posts');
        }}
      />
    );
  };

  const getIconSpecificButtonClasses = (): string => {
    if (nextButtonIcon === ANIMATABLE_ICON_CLASS) {
      return 'publish-icon-hover-animation';
    }

    return '';
  };

  const onClickContentOption = (): void => {
    dispatch(setWizardMobileNavView(WizardMobileNavView.CONTENT));
  };

  const onClickPreviewOption = (): void => {
    hidePulsatingDotForPreviewCTAIfNeeded();
    dispatch(setWizardMobileNavView(WizardMobileNavView.PREVIEW));
  };

  const getButtonsForViewToggle = (): ViewToggleBtnConfig[] => {
    const isSelectAccountsStep = currentStepFromRedux === SocialMediaWizardStep.SELECT_ACCOUNTS;
    const isAddDetailsStep = currentStepFromRedux === SocialMediaWizardStep.ADD_DETAILS;

    return [
      {
        text: isSelectAccountsStep ? window.i18next.t('pmwjs_accounts') : window.i18next.t('pmwjs_caption'),
        onClick: onClickContentOption,
        showPulsatingDot: false,
      },
      {
        text: window.i18next.t('pmwjs_preview'),
        onClick: onClickPreviewOption,
        showPulsatingDot: isAddDetailsStep || isSelectAccountsStep ? showPulsatingDotForPreviewCTA : false,
      },
    ];
  };

  const shouldShowPulsatingHighlight = (): boolean => {
    if (!shouldTriggerPreviewCTAPulses()) {
      return false;
    }

    return showHightlightPulseForViewToggle;
  };

  const getMobileViewToggle = (): ReactElement<any> | null => {
    if (currentStepFromRedux !== SocialMediaWizardStep.SELECT_DESIGN) {
      return (
        <SocialMediaWizardViewToggle
          key={currentStepFromRedux}
          buttons={getButtonsForViewToggle()}
          buttonSize={Size.XSMALL}
          showPulsatingHighlight={shouldShowPulsatingHighlight()}
        />
      );
    }
    return null;
  };

  const getNavButtons = (): ReactElement<any> | null => {
    if (isFinalStep()) {
      return getBackToMyStuffButton();
    }

    if (params.xlarge || params.large || params.medium) {
      return getNextButtonWeb();
    }

    return getMobileViewToggle();
  };

  const shouldShowPremiumUpselling = (): boolean => {
    return currentStepFromRedux === SocialMediaWizardStep.SELECT_DESIGN && !isUserPremium() && currentSelectedDesigns.length > 1;
  };

  const getNavNextButton = (): ReactElement<any> => {
    return (
      <Button
        data-testid={SocialMediaTestId.WIZARD_NEXT_BUTTON}
        customClasses={`${hideNextButton ? '_hidden' : ''} ${getIconSpecificButtonClasses()} -noun`}
        disabled={isNextButtonDisabled || areCaptionsBeingAutoGenerated}
        type={shouldShowPremiumUpselling() ? Type.PREMIUM : Type.SECONDARY}
        size={Size.SMALL}
        icon={nextButtonIcon}
        text={nextButtonText}
        onClick={shouldShowScheduleDetails() ? onSchedulingDone : onNextButtonClick}
        iconPlacement={shouldShowScheduleDetails() ? IconPlacement.LEFT : IconPlacement.RIGHT}
      />
    );
  };

  const getMultiPostUpsellToolTip = (): ReactElement<any> => {
    return (
      <div className="flex-center">
        <Icon size={IconSize.SIZE_ICON_16} icon="icon-crown" className="_unmargin-left spacing-m-r-1 spacing-m-b-1" />
        <Text size={TextSize.XSMALL} val={window.i18next.t('pmwjs_upsell_multi_posting')} />
      </div>
    );
  };

  const getNextButtonWeb = (): ReactElement<any> => {
    return (
      <div className="flex-center _fit-height _fit-width">
        {currentStepFromRedux === SocialMediaWizardStep.ADD_DETAILS && !shouldShowScheduleDetails() ? getDetailsPanelMenu() : null}
        {shouldShowScheduleDetails() ? getScheduleDetailsPanelMenu() : null}
        {shouldShowPremiumUpselling() ? (
          <OverlayTrigger
            trigger={['hover', 'focus']}
            delay={{show: 100, hide: 100}}
            overlay={
              <Tooltip className={styles.premiumTooltip} placement="top" id="tooltip-upsell">
                {getMultiPostUpsellToolTip()}
              </Tooltip>
            }
            defaultShow={undefined}
            flip={undefined}
            onHide={undefined}
            onToggle={undefined}
            placement="bottom"
            popperConfig={undefined}
            show={undefined}
            target={undefined}
          >
            <div>{getNavNextButton()}</div>
          </OverlayTrigger>
        ) : (
          getNavNextButton()
        )}
      </div>
    );
  };

  return (
    <div className={BLURABLE_CLASS_IN_AI_CAPTION_MODE}>
      <div ref={containerRef} className={`${styles.nav} _full-width flex-h-row flex-justify-between spacing-p-2 flex-align-items-center`}>
        {getLogoWithButton()}
        {getProgressBar()}
        {getNavButtons()}
      </div>
      <WizardDateTimePicker
        socialPost={socialPost}
        isOpen={isDateTimePickerVisible}
        onClose={(): void => {
          setIsDateTimePickerVisible(false);
        }}
      />
      <div className={styles.navSpacer} />
    </div>
  );
}
